import type { Objective, Project, Task } from '@portfolio-pm/shared';
import { useMemo, useState } from 'react';
import { useNow } from '../../lib/useNow.ts';
import { getDocStore } from '../../store/doc-store.ts';
import { addDays, formatDayLabel, startOfDayLocal } from './task-view-dates.ts';

const WINDOW_DAYS = 42;

const WEEKDAY_SHORT_FORMAT = new Intl.DateTimeFormat(undefined, { weekday: 'short' });

export function TimelineView({
  project,
  objectives,
  tasks,
  selectedTaskId,
  onSelectTask,
}: {
  project: Project;
  objectives: Objective[];
  tasks: Task[];
  selectedTaskId: string | undefined;
  onSelectTask(taskId: string): void;
}): JSX.Element {
  const store = getDocStore();
  const now = useNow();
  const [windowStart, setWindowStart] = useState(() => startOfDayLocal(now));
  const [selectedObjectiveId, setSelectedObjectiveId] = useState<string | undefined>(undefined);

  const days = useMemo(
    () => Array.from({ length: WINDOW_DAYS }, (_, index) => addDays(windowStart, index)),
    [windowStart],
  );
  const windowEnd = days[days.length - 1]
    ? addDays(days[days.length - 1] as number, 1)
    : windowStart;

  const visibleObjectives = objectives
    .map((objective) => ({ objective, range: getObjectiveRange(objective) }))
    .filter(
      (entry): entry is { objective: Objective; range: [number, number] } =>
        entry.range !== undefined && rangeOverlaps(entry.range, [windowStart, windowEnd]),
    );
  const visibleTasks = tasks
    .map((task) => ({ task, range: getTaskTimelineRange(task) }))
    .filter(
      (entry): entry is { task: Task; range: [number, number] } =>
        entry.range !== undefined && rangeOverlaps(entry.range, [windowStart, windowEnd]),
    );

  const undatedObjectiveCount = objectives.filter(
    (objective) => getObjectiveRange(objective) === undefined,
  ).length;
  const undatedTaskCount = tasks.filter((task) => getTaskTimelineRange(task) === undefined).length;
  const selectedObjective = selectedObjectiveId
    ? objectives.find((objective) => objective.id === selectedObjectiveId)
    : undefined;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="eyebrow-label">Timeline</p>
          <h3 className="mt-1 text-lg font-semibold text-[color:var(--app-text)]">
            {project.name} · six-week view
          </h3>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setWindowStart((value) => addDays(value, -WINDOW_DAYS))}
            className="ui-button ui-button-subtle"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setWindowStart(startOfDayLocal(now))}
            className="ui-button"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setWindowStart((value) => addDays(value, WINDOW_DAYS))}
            className="ui-button ui-button-subtle"
          >
            Next
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <span className="ui-chip">{visibleObjectives.length} dated objectives</span>
        <span className="ui-chip">{visibleTasks.length} dated tasks</span>
        <span className="ui-chip ui-chip-warning">{undatedObjectiveCount} undated objectives</span>
        <span className="ui-chip ui-chip-warning">{undatedTaskCount} undated tasks</span>
      </div>

      <div className="surface-panel min-h-0 flex-1 overflow-auto p-4">
        <div
          className="grid min-w-[calc(12rem+42*2.25rem)] gap-2"
          style={{ gridTemplateColumns: `12rem repeat(${WINDOW_DAYS}, minmax(2.25rem, 1fr))` }}
        >
          <div />
          {days.map((day) => (
            <div
              key={day}
              className="rounded-lg px-1 py-2 text-center text-[11px] text-[color:var(--app-text-soft)]"
            >
              <div>{new Date(day).getDate()}</div>
              <div>{WEEKDAY_SHORT_FORMAT.format(new Date(day))}</div>
            </div>
          ))}

          <BandHeader label="Objectives" days={WINDOW_DAYS} />
          {visibleObjectives.length === 0 ? (
            <EmptyBand message="No dated objectives in this six-week window." days={WINDOW_DAYS} />
          ) : (
            visibleObjectives.map(({ objective, range }) => (
              <TimelineRow
                key={objective.id}
                label={objective.title}
                days={WINDOW_DAYS}
                start={range[0]}
                end={range[1]}
                windowStart={windowStart}
                selected={objective.id === selectedObjectiveId}
                onSelect={() => setSelectedObjectiveId(objective.id)}
                tone="objective"
              />
            ))
          )}

          <BandHeader label="Tasks" days={WINDOW_DAYS} />
          {visibleTasks.length === 0 ? (
            <EmptyBand message="No dated tasks in this six-week window." days={WINDOW_DAYS} />
          ) : (
            visibleTasks.map(({ task, range }) => (
              <TimelineRow
                key={task.id}
                label={task.title}
                days={WINDOW_DAYS}
                start={range[0]}
                end={range[1]}
                windowStart={windowStart}
                selected={task.id === selectedTaskId}
                onSelect={() => {
                  setSelectedObjectiveId(undefined);
                  onSelectTask(task.id);
                }}
                tone="task"
              />
            ))
          )}
        </div>
      </div>

      {selectedObjective && (
        <section
          data-testid="timeline-objective-summary"
          className="surface-panel-muted grid gap-4 p-4 md:grid-cols-[minmax(0,1fr)_12rem]"
        >
          <div>
            <p className="eyebrow-label">Objective</p>
            <h4 className="mt-1 text-base font-semibold text-[color:var(--app-text)]">
              {selectedObjective.title}
            </h4>
            {selectedObjective.description && (
              <p className="mt-2 text-sm text-[color:var(--app-text-muted)]">
                {selectedObjective.description}
              </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="ui-chip">{selectedObjective.status.replace('_', ' ')}</span>
              {selectedObjective.startDate !== undefined &&
                selectedObjective.endDate !== undefined && (
                  <span className="ui-chip">
                    {formatDayLabel(selectedObjective.startDate)} →{' '}
                    {formatDayLabel(selectedObjective.endDate)}
                  </span>
                )}
            </div>
          </div>
          <div>
            <p className="ui-label">Progress</p>
            <div className="mt-2 progress-track">
              <div
                className="progress-fill"
                style={{ width: `${store.objectiveProgress(selectedObjective.id)}%` }}
              />
            </div>
            <p className="mt-2 text-sm text-[color:var(--app-text-muted)]">
              {Math.round(store.objectiveProgress(selectedObjective.id))}% rolled up progress
            </p>
          </div>
        </section>
      )}
    </div>
  );
}

function BandHeader({ label, days }: { label: string; days: number }): JSX.Element {
  return (
    <>
      <div className="border-t border-[color:var(--app-border)] pt-3 text-sm font-semibold text-[color:var(--app-text)]">
        {label}
      </div>
      <div
        className="border-t border-[color:var(--app-border)]"
        style={{ gridColumn: `2 / span ${days}` }}
      />
    </>
  );
}

function EmptyBand({ message, days }: { message: string; days: number }): JSX.Element {
  return (
    <>
      <div className="py-3 text-sm text-[color:var(--app-text-muted)]">{message}</div>
      <div
        className="surface-empty flex items-center justify-center p-4 text-sm"
        style={{ gridColumn: `2 / span ${days}` }}
      >
        {message}
      </div>
    </>
  );
}

function TimelineRow({
  label,
  start,
  end,
  windowStart,
  days,
  selected,
  onSelect,
  tone,
}: {
  label: string;
  start: number;
  end: number;
  windowStart: number;
  days: number;
  selected: boolean;
  onSelect(): void;
  tone: 'objective' | 'task';
}): JSX.Element {
  const startIndex = clampDayIndex(startOfDayLocal(start), windowStart, days);
  const endIndex = clampDayIndex(startOfDayLocal(end), windowStart, days);
  const columnStart = 2 + startIndex;
  const columnEnd = 3 + Math.max(startIndex, endIndex);
  const classes =
    tone === 'objective'
      ? 'bg-[rgba(47,110,145,0.16)] text-[color:var(--app-info)]'
      : 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-accent-text)]';

  return (
    <>
      <button
        type="button"
        onClick={onSelect}
        className={`truncate rounded-xl px-3 py-2 text-left text-sm ${
          selected
            ? 'bg-[color:var(--app-accent-soft)] text-[color:var(--app-text)]'
            : 'text-[color:var(--app-text-muted)]'
        }`}
      >
        {label}
      </button>
      <button
        type="button"
        data-testid={tone === 'objective' ? 'timeline-objective-item' : 'timeline-task-item'}
        onClick={onSelect}
        className={`relative my-2 h-8 rounded-full px-3 text-left text-xs font-medium ${classes} ${
          selected ? 'ring-2 ring-[rgba(185,108,45,0.24)]' : ''
        }`}
        style={{ gridColumn: `${columnStart} / ${columnEnd}` }}
      >
        <span className="truncate">{label}</span>
      </button>
    </>
  );
}

function getObjectiveRange(objective: Objective): [number, number] | undefined {
  if (objective.startDate !== undefined && objective.endDate !== undefined) {
    return [objective.startDate, objective.endDate];
  }
  if (objective.startDate !== undefined) return [objective.startDate, objective.startDate];
  if (objective.endDate !== undefined) return [objective.endDate, objective.endDate];
  return undefined;
}

function getTaskTimelineRange(task: Task): [number, number] | undefined {
  if (task.scheduledAt !== undefined && task.dueAt !== undefined) {
    return [task.scheduledAt, task.dueAt];
  }
  if (task.scheduledAt !== undefined) return [task.scheduledAt, task.scheduledAt];
  if (task.dueAt !== undefined) return [task.dueAt, task.dueAt];
  return undefined;
}

function rangeOverlaps(left: [number, number], right: [number, number]): boolean {
  const [leftStart, leftEnd] = left[0] <= left[1] ? left : [left[1], left[0]];
  const [rightStart, rightEnd] = right[0] <= right[1] ? right : [right[1], right[0]];
  return leftStart < rightEnd && rightStart <= leftEnd;
}

function clampDayIndex(value: number, windowStart: number, days: number): number {
  const delta = Math.floor((value - windowStart) / 86_400_000);
  return Math.max(0, Math.min(days - 1, delta));
}
